import React, { useState, useEffect, useRef } from 'react'
import { Card, Grid, CardContent, TextField, FormHelperText, Button, Box } from '@mui/material'
import LanguageStrings from '../../../../i18n/locales'
import InfoVector from '../../../../assets/InfoVector.png'
import { isValidSiteData, inValidStartOfString, inValidEndOfString } from '../../../../utils/validator'
import { styled } from '@mui/system'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import errorCrossIcon from '../../../../assets/errorCrossIcon.svg'
import CustomToast from '../ToastComponent'
import AddSiteDummy from '../../../../assets/AddSite.png'
import './EditSite.css'

const MAX_LENGTH = 100
const DeviceInformationStrings = LanguageStrings().SiteManagementCollectionStrings.AddSiteComponent

const infoCardStyles = {
  height: { xs: 'auto' },
  borderRadius: '10px',
  border: '1px solid #101010',
  backgroundColor: '#272727',
  boxShadow: '1px 1px 1px 0px rgba(0, 0, 0, 0.15)',
  marginTop: '24px'
}
const textStyle = {
  root: {
    backgroundColor: '#272727',
    '& .MuiInputBase-input': {
      color: '#F0F0F0',
      backgroundColor: '#404040'
    },
    '& .MuiInputLabel-root': {
      color: '#F0F0F0',
      '&.Mui-focused': {
        color: '#F0F0F0'
      },
      // Conditional color based on disabled state
      '&.Mui-disabled': {
        color: '#B0B0B0'
      }
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#00000000'
      },
      '&:hover fieldset': {
        borderColor: '#00000000'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#00000000'
      }
    },
    '& .Mui-disabled': {
      color: '#B0B0B0 !important'
    }
  },
  width: 'min(100%, 410px)'
}
const CustomCardContent = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '309px',
  height: '240px',
  borderRadius: '10px',
  border: '1px solid var(--General-Secondary, #303030)',
  background: 'var(--General-Secondary, #303030)',
  position: 'relative'
})
const FileInput = styled('input')({
  display: 'none'
})
const FileButton = styled(Button)({
  width: '309px',
  padding: '6px 24px',
  marginTop: '5px',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '10px',
  borderRadius: '4px',
  border:
    '1.5px solid var(--component-light-buttons-regular-primary-outline-blue-a-20071-b-3, #64C3FF)',
  background: '#202020',
  boxShadow: '1px 1px 1px 0px rgba(0, 0, 0, 0.30)'
})

interface ParentProps {
  siteName: string
  setSiteName: React.Dispatch<React.SetStateAction<string>>
  siteNameErrorInit: boolean
  setSiteNameErrorInit: React.Dispatch<React.SetStateAction<boolean>>
  description: string
  setDescription: React.Dispatch<React.SetStateAction<string>>
  descriptionErrorInit: boolean
  setDescriptionErrorInit: React.Dispatch<React.SetStateAction<boolean>>
  previewImage: string | null
  setPreviewImage: React.Dispatch<React.SetStateAction<string | null>>
  siteImage: any
  setSiteImage: React.Dispatch<React.SetStateAction<any>>
  siteId: string
}

const SiteInformation = (props: ParentProps): JSX.Element => {
  const [imageDimensions, setImageDimensions] = useState({ width: '136px', height: '136px' })
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    // istanbul ignore next
    if (props.siteImage !== undefined && props.siteImage !== null) setImageDimensions({ width: '260px', height: '215px' })
  }, [])

  const siteNameErrorMessage = (): string | null => {
    if (props?.siteName?.length > 20) {
      return DeviceInformationStrings.SiteNameLengthError
    } else if (inValidStartOfString(props?.siteName) || inValidEndOfString(props?.siteName)) {
      return DeviceInformationStrings.NotAllowedError
    } else if (!isValidSiteData(props?.siteName)) {
      return DeviceInformationStrings.SiteNameError
    }
    return null
  }
  const siteDescErrorMessage = (): string | null => {
    if (props?.description?.length > MAX_LENGTH) {
      return DeviceInformationStrings.SiteDescLengthError
    } else if (inValidStartOfString(props?.description) || inValidEndOfString(props?.description)) {
      return DeviceInformationStrings.NotAllowedError
    } else if (!isValidSiteData(props?.description)) {
      return DeviceInformationStrings.DescriptionError
    }
    return null
  }

  const isSiteNameNotValid = (): boolean => {
    return (
      !isValidSiteData(props.siteName) || props.siteName.length > 20
    )
  }
  const isDescriptionNotValid = (): boolean => {
    return (
      !isValidSiteData(props.description) || props.description.length > MAX_LENGTH
    )
  }
  // istanbul ignore next
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const files = event.target.files
    const maxSize = 100 * 1024
    const allowedTypes = ['image/jpeg', 'image/png']
    const allowedExtensions = ['jpg', 'jpeg', 'png']
    const alphanumericRegex = /^[a-zA-Z0-9]+$/
    if (files !== null && files.length > 0) {
      // Validate file size
      if (files[0].size > maxSize) {
        CustomToast({
          type: 'error',
          heading: DeviceInformationStrings.ImageSizeError,
          message: DeviceInformationStrings.ImageSizeErrorMsg
        })
        props.setPreviewImage(AddSiteDummy)
        event.target.value = '' // Clearing the file input value
        return
      }
      const fileExtension = files[0].name.split('.').pop()?.toLowerCase()
      if (!allowedTypes.includes(files[0].type) || ((fileExtension != null) && !allowedExtensions.includes(fileExtension))) {
        CustomToast({
          type: 'error',
          heading: DeviceInformationStrings.ImageTypesError,
          message: DeviceInformationStrings.ImageTypesErrorMsg
        })
        props.setPreviewImage(AddSiteDummy)
        event.target.value = '' // Clearing the file input value
        return
      }
      const fileNameWithoutExtension = files[0].name.split('.')[0]
      if (!alphanumericRegex.test(fileNameWithoutExtension)) {
        CustomToast({
          type: 'error',
          heading: 'Invalid File Name',
          message: 'File name should contain only alphanumeric characters.'
        })
        props.setPreviewImage(AddSiteDummy)
        event.target.value = '' // Clearing the file input value
        return
      }
      if (fileNameWithoutExtension.length > 20) {
        CustomToast({
          type: 'error',
          heading: 'Invalid File Name',
          message: 'File Name is greater than 20 alphanumeric characters.'
        })
        props.setPreviewImage(AddSiteDummy)
        event.target.value = '' // Clearing the file input value
        return
      }
      const reader = new FileReader()
      reader.onloadend = () => {
        const image = new Image()
        image.src = reader.result as string
        image.onload = () => {
          setImageDimensions({ width: '260px', height: '215px' }) // width: 309, height: 240
        }
        props.setPreviewImage(reader.result as string)
        props.setSiteImage(files[0])
      }
      reader.readAsDataURL(files[0])
    }
  }
  const handleBrowseClick = (): void => {
    fileInputRef.current?.click()
  }

  return (
    <div className="device-info">
      <Card sx={infoCardStyles}>
        <CardContent>
          <div className="device-info-content">
            <p className="device-info-heading">
              {DeviceInformationStrings.SiteInformation}
            </p>
          </div>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, marginTop: '10px' }}>
            <div className="image-section" style={{ backgroundColor: '#202020', marginRight: '20px', padding: '20px', width: 'max-content' }}>
              <CustomCardContent>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {/* {props.previewImage !== null && (
                    <img src={props.previewImage} alt="Preview"
                      style={{ width: imageDimensions.width, height: imageDimensions.height }}
                    />
                  )} */}
                  {/* istanbul ignore next */
                    props?.previewImage?.includes('data:') === true
                      ? <img src={props?.previewImage} alt="Preview" style={{ width: imageDimensions.width, height: imageDimensions.height }} />
                      : (props?.previewImage !== '' && props?.previewImage !== undefined)
                          ? (
                          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                          <img src={`data:image/jpeg;base64,${props?.previewImage}`} alt="Preview" style={{ width: '260px', height: '215px' }} />
                            )
                          : (
                          <img src={AddSiteDummy} alt="Preview" style={{ width: '136px', height: '136px' }} />
                            )
                    }
                </div>
                <FileInput
                  ref={fileInputRef} type="file" id="fileInput"
                  data-testid="browse-image-file"
                  accept="image/jpeg, image/png"
                  onChange={handleFileChange}
                />
              </CustomCardContent>
              <FileButton
                data-testid="browse-image-button"
                onClick={handleBrowseClick}
                style={{
                  fontFamily: 'Honeywell Sans Web', textTransform: 'unset'
                }}
              >
                Add Photo
              </FileButton>
            </div>
            <Box className="info-section" sx={{ flexGrow: 1 }}>
              <p className="device-info-heading" style={{ textTransform: 'unset', width: 'max-content' }}>
                {DeviceInformationStrings.SiteDetails}
              </p>
              <Grid container spacing={2} sx={{ width: '100%', mb: 4 }}>
                <Grid item xs={12}>
                  <span style={{ fontSize: '15px', padding: '10px 3px', display: 'block', color: '#F0F0F0' }}>
                    {DeviceInformationStrings.SiteName}
                    <sup style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500', lineHeight: '24px' }}>*</sup>
                    <Tooltip title={DeviceInformationStrings.SiteNameInfo} placement="right" arrow>
                      <IconButton size="small">
                        <img style={{ width: '12px', height: '12px', marginLeft: '5px' }} src={InfoVector} />
                      </IconButton>
                    </Tooltip>
                  </span>
                  <TextField id="custom-textfield" variant="outlined"
                    data-testId="site-name"
                    placeholder="Enter Site Name"
                    InputLabelProps={{
                      disabled: true
                    }}
                    value={props.siteName}
                    onChange={(e) => {
                      props.setSiteName(e.target.value)
                      props.setSiteNameErrorInit(true)
                    }}
                    size="small"
                    autoComplete="off"
                    className={`custom-textfield ${isSiteNameNotValid() && props.siteNameErrorInit ? 'error' : ''}`}
                    sx={{ ...textStyle.root, width: textStyle.width }}
                  />
                  {isSiteNameNotValid() && props.siteNameErrorInit && (
                    // istanbul ignore next
                    <Box style={{ display: 'flex', width: textStyle.width }}>
                      <div style={{ marginTop: '3px' }}>
                        <img src={errorCrossIcon} alt="Error" />
                      </div>
                      <FormHelperText sx={{ color: '#EE3426', paddingLeft: '5px', width: textStyle.width }} className="error-msg" >
                        {siteNameErrorMessage()}
                      </FormHelperText>
                    </Box>
                  )}
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ width: '100%', mb: 4 }}>
                <Grid item xs={12}>
                  <span style={{ fontSize: '15px', padding: '10px 3px', display: 'block', color: '#F0F0F0' }}>
                    {DeviceInformationStrings.Description}
                    <sup style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500', lineHeight: '24px' }}>*</sup>
                    <Tooltip title={DeviceInformationStrings.DescriptionInfo} placement="right" arrow>
                      <IconButton size="small">
                        <img style={{ width: '12px', height: '12px', marginLeft: '5px' }} src={InfoVector} />
                      </IconButton>
                    </Tooltip>
                  </span>
                  <TextField
                    id="custom-textfield-description"
                    variant="outlined"
                    data-testId="site-description"
                    placeholder="Enter Site Description"
                    InputLabelProps={{
                      disabled: true
                    }}
                    value={props.description}
                    onChange={(e) => {
                      props.setDescription(e.target.value)
                      props.setDescriptionErrorInit(true)
                    }}
                    size="small"
                    autoComplete="off"
                    className={`custom-textfield ${isDescriptionNotValid() && props.descriptionErrorInit ? 'error' : ''}`}
                    sx={{ ...textStyle.root, width: textStyle.width }}
                  />
                  {isDescriptionNotValid() && props.descriptionErrorInit && (
                    // istanbul ignore next
                    <Box style={{ display: 'flex', width: textStyle.width }}>
                      <div style={{ marginTop: '3px' }}>
                        <img src={errorCrossIcon} alt="Error" />
                      </div>
                      <FormHelperText sx={{ color: '#EE3426', paddingLeft: '5px' }} className="error-msg" >
                        {siteDescErrorMessage()}
                      </FormHelperText>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </div>
  )
}

export default SiteInformation
